/**
 * Barra de filtros del mapa.
 *
 * Todos los controles son `<select>` nativos: se manejan con el teclado sin
 * que haya que escribir nada, se leen bien en un lector de pantalla y en el
 * celular abren el selector del sistema. Un combo propio acá sería trabajo
 * para empeorar las tres cosas.
 *
 * El control de `min_scans` empieza en 3 y no puede bajar (ver `filters.ts`):
 * la lista de opciones directamente no contiene valores menores, así que el
 * umbral de k-anonimato no es algo que la interfaz insinúe que se puede tocar.
 */
import type { ReactNode } from 'react';

import { useI18n } from '@/i18n/useI18n';
import {
  LAYER_LABEL_KEYS,
  MAP_LAYERS,
  MIN_SCANS_FLOOR,
  PERIOD_DAYS,
  PRECISION_LABEL_KEYS,
  PRECISION_LEVELS,
  isAlertLayer,
  type MapFilters as Filters,
} from './filters';

/** Umbrales ofrecidos. El primero es el piso legal, no un valor por defecto. */
const MIN_SCANS_OPTIONS = [MIN_SCANS_FLOOR, 10, 25, 100] as const;

const SELECT_CLASS =
  'h-8 rounded border border-border-strong bg-surface px-2 text-sm text-ink';

function Field({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <label className="flex flex-col gap-1 text-xs font-medium text-ink-muted">
      {label}
      {children}
    </label>
  );
}

export interface MapFiltersBarProps {
  value: Filters;
  onChange: (next: Filters) => void;
  disabled: boolean;
}

export function MapFiltersBar({ value, onChange, disabled }: MapFiltersBarProps): ReactNode {
  const { t, fmt } = useI18n();
  const overlayUseless = isAlertLayer(value.layer);

  return (
    <div className="flex flex-wrap items-end gap-4">
      <Field label={t('map.filter.period')}>
        <select
          className={SELECT_CLASS}
          value={value.days}
          disabled={disabled}
          onChange={(event) => {
            onChange({ ...value, days: Number(event.target.value) });
          }}
        >
          {PERIOD_DAYS.map((days) => (
            <option key={days} value={days}>
              {t('map.filter.days', { days })}
            </option>
          ))}
        </select>
      </Field>

      <Field label={t('map.filter.layer')}>
        <select
          className={SELECT_CLASS}
          value={value.layer}
          disabled={disabled}
          onChange={(event) => {
            const layer = event.target.value as Filters['layer'];
            onChange({ ...value, layer });
          }}
        >
          {MAP_LAYERS.map((layer) => (
            <option key={layer} value={layer}>
              {t(LAYER_LABEL_KEYS[layer])}
            </option>
          ))}
        </select>
      </Field>

      <Field label={t('map.filter.precision')}>
        <select
          className={SELECT_CLASS}
          value={value.precision}
          disabled={disabled}
          onChange={(event) => {
            onChange({ ...value, precision: Number(event.target.value) });
          }}
        >
          {PRECISION_LEVELS.map((precision) => (
            <option key={precision} value={precision}>
              {t(PRECISION_LABEL_KEYS[precision])}
            </option>
          ))}
        </select>
      </Field>

      <Field label={t('map.filter.minScans')}>
        <select
          className={SELECT_CLASS}
          value={value.minScans}
          disabled={disabled}
          onChange={(event) => {
            onChange({ ...value, minScans: Number(event.target.value) });
          }}
        >
          {MIN_SCANS_OPTIONS.map((minScans) => (
            <option key={minScans} value={minScans}>
              {fmt.number(minScans)}
            </option>
          ))}
        </select>
      </Field>

      <label className="flex h-8 items-center gap-2 text-xs font-medium text-ink-muted">
        <input
          type="checkbox"
          className="size-4 accent-[var(--color-critical)]"
          checked={value.alertOverlay && !overlayUseless}
          disabled={disabled || overlayUseless}
          onChange={(event) => {
            onChange({ ...value, alertOverlay: event.target.checked });
          }}
        />
        {t('map.filter.alertOverlay')}
      </label>
    </div>
  );
}
